import React, { useEffect, useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./browser_data_foreman.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface DataType {
    key: string;
    foreman: string;
    code: string;
    warehouse: string;
    status: string;
}

type DataIndex = keyof DataType;
const BrowserDataForeman: React.FC = () => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [dataSource, setDataSource] = useState<DataType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        fetchForemanData();
    }, []);

    const fetchForemanData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/foreman/list`);
            const formattedData = response.data.map((item: any) => ({
                key: item.id,
                foreman: item.name,
                code: item.code,
                warehouse: item.warehouse_name,
                status: item.status
            }));

            setDataSource(formattedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Foreman',
            dataIndex: 'foreman',
            key: 'foreman',
            width: '30%',
            ...getColumnSearchProps('foreman'),
            sorter: (a, b) => a.foreman.localeCompare(b.foreman),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: '20%',
            ...getColumnSearchProps('code'),
            sorter: (a, b) => a.code.localeCompare(b.code),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            ...getColumnSearchProps('warehouse'),
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    return (

        <div id="id2o" style={transformStyle}>
            <div className="gjs-grid-row" id="i7ti">
                <div className="gjs-grid-column" id="iqoa9">
                    <Breadcrumb
                        items={[
                            { title: <HomeOutlined /> },
                            { title: (<><span>Data Foreman</span></>) },
                            { href: "#/dashboard/foreman_data", title: "Foreman" },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="iitbw">
                <div className="gjs-grid-column" id="i8f2y">
                    <Table<DataType>
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        bordered
                        size="middle"
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    navigate(`/dashboard/foreman_data?id=${record.key}`);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserDataForeman;